import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Alert,
  styled,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, HelpOutline as HelpIcon } from '@mui/icons-material';

import { ContextStore } from '../types';

interface InitialContextEditorProps {
  initialContext: ContextStore;
  onChange: (context: ContextStore) => void;
}

const InitialContextEditor: React.FC<InitialContextEditorProps> = ({
  initialContext,
  onChange,
}) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const StyledCode = styled('code')(() => ({
    margin: '0 4px',
  }));

  const handleAddContext = () => {
    if (newKey.trim()) {
      onChange({
        ...initialContext,
        [newKey]: newValue,
      });
      setNewKey('');
      setNewValue('');
    }
  };

  const handleUpdateContext = (key: string, value: string) => {
    onChange({
      ...initialContext,
      [key]: value,
    });
  };

  const handleDeleteContext = (key: string) => {
    const { [key]: deleted, ...rest } = initialContext;
    onChange(rest);
  };

  const contextEntries = Object.entries(initialContext);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6">Initial Context</Typography>
        <Tooltip title="Initial context values are available throughout the conversation flow. Use {{key}} to reference them in text.">
          <IconButton size="small">
            <HelpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {contextEntries.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No initial context values defined. Add context values that can be used throughout the
          conversation.
        </Alert>
      ) : (
        <Box mb={2}>
          {contextEntries.map(([key, value]) => (
            <Paper
              key={key}
              sx={{
                p: 2,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
              variant="outlined"
            >
              <TextField label="Key" value={key} disabled size="small" sx={{ width: 200 }} />
              <TextField
                label="Value"
                value={value as string}
                onChange={(e) => handleUpdateContext(key, e.target.value)}
                size="small"
                fullWidth
              />
              <Tooltip title="Delete context value">
                <IconButton size="small" onClick={() => handleDeleteContext(key)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2 }} variant="outlined">
        <Typography variant="subtitle2" gutterBottom>
          Add New Context Value
        </Typography>
        <Box display="flex" gap={2} alignItems="flex-end">
          <TextField
            label="Key"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            size="small"
            placeholder="e.g., userName"
            sx={{ width: 250 }}
          />
          <TextField
            label="Value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., John Doe"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddContext();
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddContext}
            disabled={!newKey.trim()}
            size="small"
            sx={{ mb: 1 }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Usage example:</strong> If you set a context value with key &quot;userName&quot;,
          you can use
          <StyledCode>{'{{userName}}'}</StyledCode> in any message or template to insert that value.
        </Typography>
      </Alert>
    </Box>
  );
};

export default InitialContextEditor;
